/**
 * Agent Settings Service
 *
 * Generic per-agent configuration, one row per (workspace_id, agent_type).
 * Backs the intelligence agents (cassandra, compass, keystone) and any future
 * agent that doesn't need a bespoke config table.
 *
 * Reads/writes agent_settings; upsert via the `upsert_agent_settings` RPC.
 */

import { getSupabaseClient } from './supabaseClient';

const TABLE = 'agent_settings';

// ── Types ─────────────────────────────────────────────────────────────────────

/** One administrator-uploaded reference document (the ATC pattern, generalized). */
export interface AgentReferenceDoc {
  id: string;
  title: string;
  docType?: string;
  content: string;
}

/** A blank row for the reference documents editor. */
export function newReferenceDoc(): AgentReferenceDoc {
  return { id: `doc-${Date.now()}`, title: '', content: '' };
}

/** Rows worth keeping: a title or some text. */
export function nonEmptyReferenceDocs(docs: AgentReferenceDoc[]): AgentReferenceDoc[] {
  return docs.filter((d) => d.title.trim() || d.content.trim());
}

/**
 * The template switches (SPEC-custom-agent-template.md §2) as stored on the
 * agent_settings row since migration 820. All optional on read for rows older
 * than the migration; the definition supplies defaults where a key is absent.
 */
export interface AgentTemplateSettings {
  triggers?: Record<string, unknown>;
  scopeDefault?: string;
  /** { <evidence source key>: enabled } */
  evidenceSources?: Record<string, boolean>;
  outputs?: string[];
  reviewRoles?: string[];
  delivery?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  referenceDocs?: AgentReferenceDoc[];
  /** Behavior-specific switches, typed by the behavior that reads them. */
  behaviorConfig?: Record<string, unknown>;
  definitionVersion?: number;
  /** The administrator who enabled the agent; automatic runs read within their visibility. */
  sponsorUserId?: string | null;
  bulkAcceptEnabled?: boolean;
  bulkAcceptFloor?: number | null;
  /** Switch group H: { temperature, maxTokens, language } inside the definition's bound. */
  modelParams?: Record<string, unknown>;
  /** The built-in behavior a template agent runs; null for agents not on the template. */
  behaviorKind?: string | null;
  /** Switch group I: { regressionGate, goldSetSize }. */
  learning?: Record<string, unknown>;
}

export interface AgentSettings extends AgentTemplateSettings {
  workspaceId: string;
  agentType: string;
  enabled: boolean;
  model: string;
  /** Custom system prompt. When set, replaces the agent's built-in default. */
  systemPrompt?: string | null;
  autoRunOnCreate: boolean;
  updatedAt: string;
}

// ── Converters ────────────────────────────────────────────────────────────────

function rowToSettings(row: Record<string, unknown>): AgentSettings {
  return {
    workspaceId:     row.workspace_id as string,
    agentType:       row.agent_type as string,
    enabled:         row.enabled as boolean,
    model:           row.model as string,
    systemPrompt:    (row.system_prompt as string | null) ?? null,
    autoRunOnCreate: (row.auto_run_on_create as boolean) ?? false,
    updatedAt:       row.updated_at as string,
    triggers:        (row.triggers as Record<string, unknown> | undefined) ?? undefined,
    scopeDefault:    (row.scope_default as string | undefined) ?? undefined,
    evidenceSources: (row.evidence_sources as Record<string, boolean> | undefined) ?? undefined,
    outputs:         (row.outputs as string[] | undefined) ?? undefined,
    reviewRoles:     (row.review_roles as string[] | undefined) ?? undefined,
    delivery:        (row.delivery as Record<string, unknown> | undefined) ?? undefined,
    limits:          (row.limits as Record<string, unknown> | undefined) ?? undefined,
    referenceDocs:   (row.reference_docs as AgentReferenceDoc[] | undefined) ?? undefined,
    behaviorConfig:  (row.behavior_config as Record<string, unknown> | undefined) ?? undefined,
    definitionVersion: (row.definition_version as number | undefined) ?? undefined,
    sponsorUserId:   (row.sponsor_user_id as string | null | undefined) ?? null,
    bulkAcceptEnabled: (row.bulk_accept_enabled as boolean | undefined) ?? false,
    bulkAcceptFloor: row.bulk_accept_floor == null ? null : Number(row.bulk_accept_floor),
    modelParams:     (row.model_params as Record<string, unknown> | undefined) ?? undefined,
    behaviorKind:    (row.behavior_kind as string | null | undefined) ?? null,
    learning:        (row.learning as Record<string, unknown> | undefined) ?? undefined,
  };
}

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Fetch the settings row for an agent in a workspace.
 * Returns null if not yet configured.
 */
export async function getAgentSettings(
  workspaceId: string,
  agentType: string,
): Promise<{ data: AgentSettings | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('agent_type', agentType)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: null };

    return { data: rowToSettings(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * The settings rows for several agents in one workspace, keyed by agent type.
 * An agent with no row is absent from the map (it has never been configured).
 */
export async function listAgentSettings(
  workspaceId: string,
  agentTypes: string[],
): Promise<{ data: Record<string, AgentSettings>; error: string | null }> {
  if (!agentTypes.length) return { data: {}, error: null };
  try {
    const { data, error } = await getSupabaseClient()
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('agent_type', agentTypes);
    if (error) return { data: {}, error: error.message };
    const out: Record<string, AgentSettings> = {};
    for (const row of (data ?? []) as Record<string, unknown>[]) {
      const settings = rowToSettings(row);
      out[settings.agentType] = settings;
    }
    return { data: out, error: null };
  } catch (err) {
    return { data: {}, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export interface UpsertAgentSettingsInput extends AgentTemplateSettings {
  workspaceId: string;
  agentType: string;
  enabled: boolean;
  model: string;
  systemPrompt?: string | null;
  autoRunOnCreate?: boolean;
  updatedBy?: string;
}

export interface PatchAgentSettingsEnabledInput {
  workspaceId: string;
  agentType: string;
  enabled: boolean;
  updatedBy?: string;
}

/**
 * Patch only the `enabled` flag on an existing agent_settings row.
 * Creates the row if it doesn't exist (uses upsert_agent_settings RPC with the
 * current model preserved, defaulting to an empty string when creating fresh).
 */
export async function patchAgentSettingsEnabled(
  input: PatchAgentSettingsEnabledInput,
): Promise<{ data: AgentSettings | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    // Read current row so we can preserve model/systemPrompt on upsert.
    const { data: current } = await supabase
      .from(TABLE)
      .select('model, system_prompt, auto_run_on_create')
      .eq('workspace_id', input.workspaceId)
      .eq('agent_type', input.agentType)
      .maybeSingle();

    const { data, error } = await supabase.rpc('upsert_agent_settings', {
      p_workspace_id:       input.workspaceId,
      p_agent_type:         input.agentType,
      p_enabled:            input.enabled,
      p_model:              (current as Record<string, unknown> | null)?.model as string ?? '',
      p_system_prompt:      (current as Record<string, unknown> | null)?.system_prompt as string | null ?? null,
      p_updated_by:         input.updatedBy ?? null,
      p_auto_run_on_create: (current as Record<string, unknown> | null)?.auto_run_on_create as boolean ?? false,
    });

    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: 'No data returned' };

    return { data: rowToSettings(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function upsertAgentSettings(input: UpsertAgentSettingsInput): Promise<{
  data: AgentSettings | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('upsert_agent_settings', {
      p_workspace_id:       input.workspaceId,
      p_agent_type:         input.agentType,
      p_enabled:            input.enabled,
      p_model:              input.model,
      p_system_prompt:      input.systemPrompt ?? null,
      p_updated_by:         input.updatedBy ?? null,
      p_auto_run_on_create: input.autoRunOnCreate ?? false,
      // Template switches (migration 820). Null means "keep what is stored".
      p_triggers:            input.triggers ?? null,
      p_scope_default:       input.scopeDefault ?? null,
      p_evidence_sources:    input.evidenceSources ?? null,
      p_outputs:             input.outputs ?? null,
      p_review_roles:        input.reviewRoles ?? null,
      p_delivery:            input.delivery ?? null,
      p_limits:              input.limits ?? null,
      p_reference_docs:      input.referenceDocs ?? null,
      p_behavior_config:     input.behaviorConfig ?? null,
      p_sponsor_user_id:     input.sponsorUserId ?? null,
      p_bulk_accept_enabled: input.bulkAcceptEnabled ?? null,
      p_bulk_accept_floor:   input.bulkAcceptFloor ?? null,
      p_model_params:        input.modelParams ?? null,
      p_behavior_kind:       input.behaviorKind ?? null,
      p_learning:            input.learning ?? null,
    });

    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: 'No data returned from upsert' };

    return { data: rowToSettings(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
